import React, { useRef } from "react";
import { Box } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import { DraggablePickingCard } from "./DraggablePickingCard";

const ESTIMATED_CARD_HEIGHT = 128;
const VIRTUALIZE_THRESHOLD = 30;

type VirtualizedColumnItemsProps = {
  items: any[];
  stageId: number | string;
  stageColor?: string;
  userTimezone: string;
  selectedSet: Set<number>;
  onToggleItem: (itemId: number, checked: boolean) => void;
  dragDisabled?: boolean;
};

export function VirtualizedColumnItems({
  items: rawItems,
  stageId,
  stageColor,
  userTimezone,
  selectedSet,
  onToggleItem,
  dragDisabled = false,
}: VirtualizedColumnItemsProps) {
  const items = Array.isArray(rawItems) ? rawItems : [];
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_CARD_HEIGHT,
    overscan: 5,
  });

  if (items.length <= VIRTUALIZE_THRESHOLD) {
    return (
      <>
        {items.map((item) => (
          <DraggablePickingCard
            key={item.id}
            item={item}
            stageId={stageId}
            stageColor={stageColor}
            userTimezone={userTimezone}
            isSelected={selectedSet.has(item.id)}
            onToggle={onToggleItem}
            disabled={dragDisabled}
          />
        ))}
      </>
    );
  }

  return (
    <Box
      ref={scrollRef}
      sx={{
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        pr: 0.5,
        scrollbarWidth: "thin",
        scrollbarColor: "#cbd5e1 transparent",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#cbd5e1",
          borderRadius: "999px",
        },
      }}
    >
      <Box
        sx={{
          height: virtualizer.getTotalSize(),
          width: "100%",
          position: "relative",
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const item = items[virtualItem.index];
          return (
            <Box
              key={item.id}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <DraggablePickingCard
                item={item}
                stageId={stageId}
                stageColor={stageColor}
                userTimezone={userTimezone}
                isSelected={selectedSet.has(item.id)}
                onToggle={onToggleItem}
                disabled={dragDisabled}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export { VIRTUALIZE_THRESHOLD };
